import type { Core } from 'chessboard/core/config/types';
import { RendererErrorCatcher } from 'plugins/renderer-error-catcher/config/types';
import { Logger } from 'api/logger/config/types';

/**
 * Reload the game position when there is an error emanating from the renderer method.
 */
export function reloadOnError({
  context,
  method,
}: {
  context: Core.Context;
  method: Function;
}) {
  return function wrappedRendererMethod(...args: any[]) {
    try {
      return method(...args);
    } catch (e) {
      const { api, renderer } = context;

      // If the JCE game state and the renderer state get de-synchronised (rare but possible), then
      // it is possible for the renderer to throw an error if it can't find a piece it is supposed
      // to move. Previously this caused execution to stop, very prominently displaying our bug to
      // the user. The solution here is to reload according to the current PGN, ensuring that the
      // game state and renderer state are re-synchronised, and swallowing the error so that the
      // user is unaffected.
      // eslint-disable-next-line no-console
      console.error(
        'Board position reloaded due to game and renderer state desynchronisation:\n',
        e,
      );

      api.logger.log({
        code: RendererErrorCatcher.ErrorCodes.StateDesynchronisation,
        data: e,
        level: Logger.Levels.Error,
      });

      renderer?.loadPieces({ pieces: api.getPieces(), reloadAll: true }, context);
    }
  };
}
